import type { JiraTransition } from '@/types';

import { AddTransitionDropdown } from './AddTransitionDropdown';
import { TransitionItem } from './TransitionItem';

export interface TransitionListProps {
  transitions: JiraTransition[];
  selectedTransitionIds: string[];
  availableTransitions: JiraTransition[];
  onChange: (transitionIds: string[]) => void;
  disabled?: boolean;
}

export const TransitionList = ({
  transitions,
  selectedTransitionIds,
  availableTransitions,
  onChange,
  disabled = false,
}: TransitionListProps) => {
  const selectedTransitions = selectedTransitionIds
    .map(id => transitions.find(t => t.id === id))
    .filter((t): t is JiraTransition => t !== undefined);

  const handleMoveUp = (index: number) => {
    if (index === 0) return;
    const newIds = [...selectedTransitionIds];
    [newIds[index - 1], newIds[index]] = [newIds[index], newIds[index - 1]];
    onChange(newIds);
  };

  const handleMoveDown = (index: number) => {
    if (index === selectedTransitionIds.length - 1) return;
    const newIds = [...selectedTransitionIds];
    [newIds[index], newIds[index + 1]] = [newIds[index + 1], newIds[index]];
    onChange(newIds);
  };

  const handleRemove = (index: number) => {
    const newIds = selectedTransitionIds.filter((_, i) => i !== index);
    onChange(newIds);
  };

  const handleAdd = (transitionId: string) => {
    onChange([...selectedTransitionIds, transitionId]);
  };

  return (
    <div className="flex flex-col gap-2">
      {selectedTransitions.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center">
          <p className="text-sm text-gray-500">
            No transitions selected. Add transitions below.
          </p>
        </div>
      ) : (
        <ol className="flex flex-col gap-2">
          {selectedTransitions.map((transition, index) => (
            <TransitionItem
              key={transition.id}
              transition={transition}
              index={index}
              isFirst={index === 0}
              isLast={index === selectedTransitions.length - 1}
              onMoveUp={() => handleMoveUp(index)}
              onMoveDown={() => handleMoveDown(index)}
              onRemove={() => handleRemove(index)}
              disabled={disabled}
            />
          ))}
        </ol>
      )}

      <AddTransitionDropdown
        availableTransitions={availableTransitions}
        onAdd={handleAdd}
        disabled={disabled}
      />
    </div>
  );
};
